import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Tooltip,
  Slide,
  useMediaQuery,
  useTheme,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAllAnnonces } from "../store/annonceSlice";
import useCompare from "../hooks/useCompare";
import { renderDoc } from "../requests/s3";

type AnnoncePreview = {
  id: string;
  titre: string;
  prix: number;
  image: string;
};

const CompareBar = memo(() => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { compareList, compareCount, removeFromCompare, clearCompare } = useCompare();
  const allAnnonces = useSelector(selectAllAnnonces);
  const [previews, setPreviews] = useState<AnnoncePreview[]>([]);

  // Charge les aperçus des annonces sélectionnées
  useEffect(() => {
    const loadPreviews = async () => {
      const newPreviews: AnnoncePreview[] = [];

      for (const id of compareList) {
        const annonce = allAnnonces.find((a) => a._id === id);
        if (annonce) {
          let imageUrl = "";
          if (annonce.data.thumbnail?.imageUrl) {
            try {
              const res = await renderDoc(annonce.data.thumbnail.imageUrl);
              if (res !== "error") {
                imageUrl = res.url;
              }
            } catch {
              // Ignore image loading errors
            }
          }

          newPreviews.push({
            id: annonce._id,
            titre: annonce.data.titre || "Annonce",
            prix: annonce.data.data.informations.prix.data,
            image: imageUrl,
          });
        }
      }

      setPreviews(newPreviews);
    };

    if (compareList.length > 0) {
      loadPreviews();
    } else {
      setPreviews([]);
    }
  }, [compareList, allAnnonces]);

  // Ne pas afficher si la liste est vide
  if (compareCount === 0) {
    return null;
  }

  // Version mobile compacte
  if (isMobile) {
    return (
      <Slide direction="up" in={compareCount > 0} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: 1.5,
            backgroundColor: "white",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            {/* Avatars empilés */}
            <Box display="flex" alignItems="center" gap={1}>
              <Box display="flex" sx={{ "& > *:not(:first-of-type)": { ml: -1.5 } }}>
                {previews.map((preview, index) => (
                  <Avatar
                    key={preview.id}
                    src={preview.image}
                    variant="rounded"
                    sx={{
                      width: 36,
                      height: 36,
                      border: "2px solid white",
                      zIndex: previews.length - index,
                    }}
                  >
                    {preview.titre.charAt(0)}
                  </Avatar>
                ))}
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {compareCount}/3
              </Typography>
            </Box>

            {/* Actions */}
            <Box display="flex" alignItems="center" gap={0.5}>
              <IconButton size="small" onClick={clearCompare}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
              <Button
                variant="contained"
                size="small"
                disabled={compareCount < 2}
                onClick={() => navigate("/comparer")}
                sx={{ minWidth: "auto", px: 2 }}
              >
                Comparer
              </Button>
            </Box>
          </Box>
        </Paper>
      </Slide>
    );
  }

  // Version desktop
  return (
    <Slide direction="up" in={compareCount > 0} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          p: 2,
          backgroundColor: "white",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          {/* En-tête */}
          <Box display="flex" alignItems="center" gap={1}>
            <CompareArrowsIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Comparer ({compareCount}/3)
            </Typography>
          </Box>

          {/* Aperçus des biens */}
          <Box display="flex" gap={1} flexWrap="wrap" flex={1} justifyContent="center">
            {previews.map((preview) => (
              <Box
                key={preview.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "grey.100",
                  borderRadius: 2,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Avatar
                  src={preview.image}
                  variant="rounded"
                  sx={{ width: 40, height: 40 }}
                >
                  {preview.titre.charAt(0)}
                </Avatar>
                <Box sx={{ maxWidth: 120 }}>
                  <Typography
                    variant="caption"
                    noWrap
                    display="block"
                    fontWeight={500}
                  >
                    {preview.titre}
                  </Typography>
                  <Typography variant="caption" color="primary">
                    {new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "EUR",
                      maximumFractionDigits: 0,
                    }).format(preview.prix)}
                  </Typography>
                </Box>
                <Tooltip title="Retirer">
                  <IconButton
                    size="small"
                    onClick={() => removeFromCompare(preview.id)}
                    sx={{ ml: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}

            {/* Emplacements vides */}
            {Array.from({ length: 3 - compareCount }).map((_, index) => (
              <Box
                key={`empty-${index}`}
                sx={{
                  width: 180,
                  height: 52,
                  border: "2px dashed",
                  borderColor: "grey.300",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Sélectionnez un bien
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Actions */}
          <Box display="flex" gap={1}>
            <Tooltip title="Vider la sélection">
              <IconButton onClick={clearCompare} color="default">
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              disabled={compareCount < 2}
              onClick={() => navigate("/comparer")}
              startIcon={<CompareArrowsIcon />}
            >
              Comparer
            </Button>
          </Box>
        </Box>

        {compareCount < 2 && (
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            display="block"
            mt={1}
          >
            Sélectionnez au moins 2 biens pour les comparer
          </Typography>
        )}
      </Paper>
    </Slide>
  );
});

CompareBar.displayName = "CompareBar";

export default CompareBar;
